/**
 * ViewToggle — segmented control to switch between List and Calendar views.
 * Uses Radix Tabs primitive for keyboard accessibility (Tab to enter,
 * Left/Right arrows to switch, Enter/Space to activate).
 *
 * The active tab is visually differentiated with a filled background
 * (not color-only), satisfying WCAG requirements.
 *
 * Validates: Requirements 1.1, 1.4, 10.5
 */

import React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ViewToggleProps {
  readonly activeView: 'list' | 'calendar';
  readonly onViewChange: (view: 'list' | 'calendar') => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ViewToggle({
  activeView,
  onViewChange,
}: ViewToggleProps): React.ReactElement {
  return (
    <TabsPrimitive.Root
      value={activeView}
      onValueChange={(value) => onViewChange(value as 'list' | 'calendar')}
    >
      <TabsPrimitive.List
        className="inline-flex h-8 items-center rounded-md bg-muted p-0.5 text-sm"
        aria-label="View mode"
      >
        <TabsPrimitive.Trigger
          value="list"
          className="inline-flex items-center justify-center rounded-sm px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground"
        >
          List
        </TabsPrimitive.Trigger>
        <TabsPrimitive.Trigger
          value="calendar"
          className="inline-flex items-center justify-center rounded-sm px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground"
        >
          Calendar
        </TabsPrimitive.Trigger>
      </TabsPrimitive.List>
    </TabsPrimitive.Root>
  );
}
